import { useEffect, useMemo, useState } from 'react';
import { Item, Bin, Packer } from '../../../../../utils/bin-packing-v6/3D';
import { LoadExampleModalLocal } from './load-example-modal-local';
import * as St from './index.styled';

/**
 * 적재 예시 3D 뷰어 (로컬 알고리즘 버전).
 *
 * 기존 WegetPalletCanvas(../weget-pallet-canvas-v2)는 백엔드 `/pallets/calculate-stack`
 * API로 적재 좌표를 받아왔지만, 현재 그 API를 사용하지 않는다.
 * 이 컴포넌트는 적재 좌표를 프론트의 빈패킹 v6 엔진(파레트 자동생성과 동일한 엔진)으로
 * 직접 계산하고, 전용 모달 셸/3D 렌더러로 그린다.
 *
 * 프롭 계약은 기존 WegetPalletCanvas와 동일하다 (drop-in 교체 가능).
 *   open, onClose, loadItems, palletUUID
 */
const FACTOR = 1e5; // bin-packing-v6 factoredInteger 스케일 (1 factored unit = 1e-5 mm)

// 상품별 색상 팔레트 (같은 상품은 항상 같은 색). 구분 잘 되는 중간 톤 위주.
const PALETTE = [
    '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6',
    '#14b8a6', '#ec4899', '#f97316', '#6366f1', '#84cc16',
    '#06b6d4', '#a855f7', '#eab308', '#22c55e', '#f43f5e',
];

const DEFAULT_ALLOWED_SIZE = { width: 1100, height: 1400, depth: 1100 };

export function WegetPalletCanvasLocal({
    open,
    onClose,
    loadItems,
    palletUUID,
}) {
    const targetLoadItems = loadItems?.filter(loadItem => loadItem.palletUUID === palletUUID);

    const [fitedBoxs, setFitedBoxs] = useState([]);
    const [allowedSize, setAllowedSize] = useState(DEFAULT_ALLOWED_SIZE);

    useEffect(() => {
        // 최초 진입 시 기본 허용 규격으로 계산
        setFitedBoxs(packLoadItemsLocally(targetLoadItems, DEFAULT_ALLOWED_SIZE));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [palletUUID]);

    const handleApplyAllowedSize = (nextAllowedSize) => {
        setAllowedSize(nextAllowedSize);
        setFitedBoxs(packLoadItemsLocally(targetLoadItems, nextAllowedSize));
    };

    // 상품별 색상 + 수량 집계 (범례/렌더러 공유)
    const { colorMap, legend } = useMemo(() => {
        const map = new Map();
        const countMap = new Map();
        (fitedBoxs || []).forEach(box => {
            const name = box.itemName;
            if (!map.has(name)) {
                map.set(name, PALETTE[map.size % PALETTE.length]);
            }
            countMap.set(name, (countMap.get(name) || 0) + 1);
        });
        const legendList = Array.from(countMap.entries()).map(([name, qty]) => ({
            name,
            qty,
            color: map.get(name),
        }));
        return { colorMap: map, legend: legendList };
    }, [fitedBoxs]);

    const totalBoxCount = fitedBoxs?.length || 0;

    return (
        <St.Backdrop onClick={() => onClose()}>
            <St.Modal onClick={(e) => e.stopPropagation()}>
                <St.Header>
                    <div className='titleGroup'>
                        <span className='title'>적재 예시</span>
                        <span className='subtitle'>빈패킹 알고리즘 기반 3D 적재 시뮬레이션</span>
                    </div>
                    <div className='spacer' />
                    <div className='countBadge'>적재 <b>{totalBoxCount}</b> 박스</div>
                    <St.CloseButton type='button' onClick={() => onClose()}>×</St.CloseButton>
                </St.Header>

                {legend.length > 0 &&
                    <St.Legend>
                        {legend.map(item => (
                            <span className='chip' key={item.name}>
                                <span className='dot' style={{ backgroundColor: item.color }} />
                                <span className='name'>{item.name}</span>
                                <span className='qty'>×{item.qty}</span>
                            </span>
                        ))}
                    </St.Legend>
                }

                <St.CanvasArea>
                    {totalBoxCount > 0
                        ? <>
                            <LoadExampleModalLocal
                                allowedSize={allowedSize}
                                fitedBoxs={fitedBoxs}
                                colorMap={colorMap}
                            />
                            <div className='hint'>드래그로 회전 · 휠로 확대/축소</div>
                        </>
                        : <div className='empty'>
                            <span>이 파레트에 적재할 박스가 없습니다.</span>
                            <span>허용 규격을 조정해 다시 시도해 보세요.</span>
                        </div>
                    }
                </St.CanvasArea>

                <AllowedSizeField
                    defaultAllowedSize={DEFAULT_ALLOWED_SIZE}
                    onApply={handleApplyAllowedSize}
                />
            </St.Modal>
        </St.Backdrop>
    );
}

/**
 * 파레트에 담긴 loadItem들을 빈패킹 v6로 적재해 3D 렌더러가 소비하는 형태로 변환한다.
 * 반환 형태: { id, itemName, coordinates:{x1..z2} } (mm 단위)
 *
 * 상자 준비 방식은 파레트 자동생성(BoxUtil.packToPallet)과 동일하게 맞춰,
 * 예시가 실제 생성된 파레트 적재와 일치하도록 한다:
 *   - 치수를 내림차순 정렬해 w=최장 / d=중간 / h=최단 으로 넣는다.
 *   - boxQuantity 만큼 낱개 상자로 펼친다.
 *   - shouldItemBeFlat 로 눕혀야 하는 상자를 판정한다.
 */
function packLoadItemsLocally(targetLoadItems, allowedSize) {
    const packer = new Packer();
    // BoxUtil.packToPallet 과 동일한 Bin 구성 (6번째 인자는 무시되지만 시그니처 보존)
    const bin = new Bin(
        'PALLET',
        Number(allowedSize?.width) || 0,
        Number(allowedSize?.height) || 0,
        Number(allowedSize?.depth) || 0,
        10000,
        100
    );
    packer.addBin(bin);

    // 낱개 상자 name → 렌더링 메타(상품명-옵션명) 매핑
    const nameToItemName = new Map();

    targetLoadItems
        ?.filter(r => r.boxQuantity > 0)
        ?.forEach(r => {
            // 가장 긴 변을 width, 두번째를 depth, 가장 짧은 변을 height 로 정렬 (BoxUtil 동일)
            const boxSize = [r?.boxWidth, r?.boxDepth, r?.boxHeight].sort((a, b) => b - a);
            const w = boxSize[0];
            const d = boxSize[1];
            const h = boxSize[2];
            const mustBeFlat = shouldItemBeFlat(w, h, d);
            const itemName = `${r?.productName}-${r?.optionName}`;

            for (let i = 0; i < r.boxQuantity; i++) {
                const name = `${r.uuid}__${i}`;
                nameToItemName.set(name, itemName);
                // Item(name, w, h, d, weight, allowedRotation, mustBeFlat)
                packer.addItem(new Item(name, w, h, d, null, null, mustBeFlat));
            }
        });

    packer.pack();

    const packedBin = packer.bins?.[0];
    const placedItems = packedBin?.items || [];

    return placedItems.map(item => {
        const dim = item.getDimension(); // factored [width, height(수직), depth]
        const pos = item.position;       // factored [x, y(수직), z] min-corner

        // factored(×1e5) → mm 로 환원. 렌더러가 다시 /1000 하여 m 로 렌더한다.
        const x1 = pos[0] / FACTOR;
        const y1 = pos[1] / FACTOR;
        const z1 = pos[2] / FACTOR;

        return {
            id: item.name,
            itemName: nameToItemName.get(item.name),
            coordinates: {
                x1: x1,
                y1: y1,
                z1: z1,
                x2: x1 + dim[0] / FACTOR,
                y2: y1 + dim[1] / FACTOR,
                z2: z1 + dim[2] / FACTOR,
            },
        };
    });
}

function AllowedSizeField({
    defaultAllowedSize,
    onApply,
}) {
    const [allowedSize, setAllowedSize] = useState(defaultAllowedSize);

    return (
        <St.Controls>
            <div className='fields'>
                <label className='field'>
                    <span>허용 너비</span>
                    <div className='inputBox'>
                        <input
                            type='number'
                            inputMode='numeric'
                            value={allowedSize.width}
                            onChange={(e) => setAllowedSize({ ...allowedSize, width: e.target.value })}
                        />
                        <em>mm</em>
                    </div>
                </label>
                <label className='field'>
                    <span>허용 깊이</span>
                    <div className='inputBox'>
                        <input
                            type='number'
                            inputMode='numeric'
                            value={allowedSize.depth}
                            onChange={(e) => setAllowedSize({ ...allowedSize, depth: e.target.value })}
                        />
                        <em>mm</em>
                    </div>
                </label>
                <label className='field'>
                    <span>허용 높이</span>
                    <div className='inputBox'>
                        <input
                            type='number'
                            inputMode='numeric'
                            value={allowedSize.height}
                            onChange={(e) => setAllowedSize({ ...allowedSize, height: e.target.value })}
                        />
                        <em>mm</em>
                    </div>
                </label>
            </div>
            <button
                type='button'
                className='apply'
                onClick={() => onApply(allowedSize)}
            >
                적용
            </button>
        </St.Controls>
    );
}

/**
 * 상자를 눕혀 놓아야 하는지 판정한다. (BoxUtil.packToPallet 와 동일 기준)
 * 높이가 절대적으로 낮거나(200mm 미만), 다른 치수 대비 극단적으로 낮으면 눕힌다.
 */
function shouldItemBeFlat(W, H, D, stableHeightThreshold = 200) {
    if (H < stableHeightThreshold) {
        return true;
    }

    const smallestHorizontalDim = Math.min(W, D);
    if (H < 0.5 * smallestHorizontalDim) {
        return true;
    }

    return false;
}
